<template>
  <div class="flex gap-0 bg-slate-100 max-md:flex-wrap">
    <div class="flex flex-col flex-1 max-md:max-w-full">
      <div class="flex flex-col items-start self-center pt-7 pb-12 mt-8 w-full bg-white rounded-3xl max-w-[1285px] max-md:px-5 max-md:max-w-full">
        <!-- Header -->
        <div class="flex items-center justify-between w-full text-2xl font-medium text-neutral-800 mb-6 px-6">
          <div class="flex items-center gap-4">
            <img
              loading="lazy"
              src="@/assets/img/brand.svg"
              class="aspect-[0.95] w-[21px]"
            />
            <div class="text-xs sm:text-sm md:text-[22px] font-semibold">
              Store Locator Configuration
            </div>
          </div>
          <!-- Embed Widget Button -->
          <div class="flex-shrink-0">
            <embed-widget type="store-locator" />
          </div>
        </div>



        <!-- Preview Section -->
        <div class="w-full mb-8">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-800">Live Preview</h3>
            <button
              @click="refreshPreview"
              :disabled="isRefreshingPreview"
              class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              style="flex: 0 0 auto;"
            >
              <svg v-if="isRefreshingPreview" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              {{ isRefreshingPreview ? 'Refreshing...' : 'Refresh Preview' }}
            </button>
          </div>
          <p class="text-sm text-gray-600 mb-4">The preview below shows how your store locator will appear with the current configuration. Changes are applied automatically as you configure the settings.</p>
          <div class="border-2 border-gray-200 rounded-lg overflow-auto" style="height: 600px;">
            <StoreLocator
              v-if="WID"
              ref="storeLocatorComponent"
              :wid="WID"
              :storeLocatorStyles="config"
              :disableNavigation="true"
              :disableFavicon="true"
              :disablePageTitle="true"
              :key="storeRefreshKey"
            />
            <div v-else class="flex items-center justify-center h-full text-gray-400">
              <div class="text-center">
                <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                <p class="text-lg font-medium text-gray-500 mb-2">No Workspace Found</p>
                <p class="text-sm text-gray-400">Please ensure you have a valid workspace to preview the store locator.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Configuration Section -->
        <div class="w-full">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Configuration Options</h3>
          
          <!-- Tab Navigation -->
          <div class="flex border-b border-gray-200 mb-6">
            <button
              v-for="tab in configurationTabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Tab Content -->
          <div class="min-h-[600px]">
            <!-- Main Tab -->
            <div v-if="activeTab === 'main'" class="space-y-8">
              <!-- Color Scheme Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Color Scheme
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Primary Color -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Primary Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.primaryColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.primaryColor"
                        placeholder="#1773EA"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Main brand color used throughout the interface</p>
                  </div>

                  <!-- Secondary Color -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Secondary Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.secondaryColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.secondaryColor"
                        placeholder="#6B7280"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Supporting color for secondary elements</p>
                  </div>
                </div>
              </div>



              <!-- Subdomain Management Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"></path>
                  </svg>
                  Subdomain Management
                </h4>
                <div class="space-y-4">
                  <p class="text-sm text-gray-600 mb-4">Configure your custom subdomain for the store locator. This will create a unique URL for your store locator.</p>
                  
                  <!-- Current Subdomain Display -->
                  <div v-if="currentSubdomain" class="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-green-800">Current Subdomain</h5>
                        <p class="text-lg font-semibold text-green-900">
                          <a 
                            :href="`https://${getSubdomainUrl(currentSubdomain, 'stores')}`" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="text-green-900 hover:text-green-700 underline"
                          >
                            {{ getSubdomainUrl(currentSubdomain, 'stores') }}
                            <v-icon size="16" class="ml-1">mdi-open-in-new</v-icon>
                          </a>
                        </p>
                        <p class="text-xs text-green-600 mt-1">Click the URL above to preview your store locator</p>
                      </div>
                      <div class="flex gap-2 justify-end">
                        <a 
                          :href="`https://${getSubdomainUrl(currentSubdomain, 'stores')}`" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                          style="color: white !important;"
                        >
                          Preview
                        </a>
                        <button
                          @click="openSubdomainDialog"
                          class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          Edit Subdomain
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- No Subdomain State -->
                  <div v-else class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-yellow-800">No Subdomain Configured</h5>
                        <p class="text-sm text-yellow-700">Create a custom subdomain for your store locator</p>
                      </div>
                      <button
                        @click="openSubdomainDialog"
                        class="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium"
                      >
                        Create Subdomain
                      </button>
                    </div>
                  </div>

                  <!-- Subdomain Information -->
                  <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 class="text-sm font-medium text-blue-800 mb-2">About Subdomains</h5>
                    <ul class="text-xs text-blue-700 space-y-1">
                      <li>• Your store locator will be accessible at: <strong>{{ getSubdomainUrl('your-subdomain', 'stores') }}</strong></li>
                      <li>• Subdomains can only contain letters, numbers, and hyphens</li>
                      <li>• Must be between 3-63 characters long</li>
                      <li>• Cannot start or end with a hyphen</li>
                      <li>• Changes may take a few minutes to propagate</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Custom Domain Management Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"></path>
                  </svg>
                  Custom Domain Management
                </h4>
                <div class="space-y-4">
                  <p class="text-sm text-gray-600 mb-4">Connect your own domain to your store locator for a professional branded experience.</p>
                  
                  <!-- Current Custom Domain Display -->
                  <div v-if="currentCustomDomain" class="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-green-800">Current Custom Domain</h5>
                        <p class="text-lg font-semibold text-green-900">
                          <a 
                            :href="`https://${currentCustomDomain}`" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="text-green-900 hover:text-green-700 underline"
                          >
                            {{ currentCustomDomain }}
                            <v-icon size="16" class="ml-1">mdi-open-in-new</v-icon>
                          </a>
                        </p>
                        <p class="text-xs text-green-600 mt-1">Your custom domain has been configured. Please ensure DNS records are properly set up.</p>
                      </div>
                      <div class="flex gap-2 justify-end">
                        <a 
                          :href="`https://${currentCustomDomain}`" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                          style="color: white !important;"
                        >
                          Preview
                        </a>
                        <button
                          @click="openCustomDomainDialog"
                          class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          Edit Domain
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- No Custom Domain State -->
                  <div v-else class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-yellow-800">No Custom Domain Configured</h5>
                        <p class="text-sm text-yellow-700">Connect your own domain for a professional branded experience</p>
                      </div>
                      <button
                        @click="openCustomDomainDialog"
                        class="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium"
                      >
                        Connect Domain
                      </button>
                    </div>
                  </div>

                  <!-- Custom Domain Setup Instructions -->
                  <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h5 class="text-sm font-medium text-purple-800 mb-3 flex items-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      How to Connect Your Custom Domain
                    </h5>
                    <div class="space-y-3">
                      <div class="bg-white rounded-lg p-3 border border-purple-100">
                        <h6 class="text-sm font-medium text-purple-900 mb-2">Step 1: Add Your Domain</h6>
                        <p class="text-xs text-purple-700">Enter your domain (e.g., <code class="bg-purple-100 px-1 rounded">stores.yourdomain.com</code>) in the domain field above.</p>
                      </div>
                      <div class="bg-white rounded-lg p-3 border border-purple-100">
                        <h6 class="text-sm font-medium text-purple-900 mb-2">Step 2: Configure DNS Records</h6>
                        <p class="text-xs text-purple-700 mb-2">In your domain's DNS settings, add a CNAME record (sample values shown below):</p>
                        <div class="bg-gray-50 rounded-lg overflow-hidden">
                          <table class="w-full text-xs font-mono">
                            <thead class="bg-gray-100">
                              <tr>
                                <th class="px-3 py-2 text-left text-gray-600 font-medium">Record Type</th>
                                <th class="px-3 py-2 text-left text-gray-600 font-medium">Name/Host</th>
                                <th class="px-3 py-2 text-left text-gray-600 font-medium">Value/Target</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr class="border-t border-gray-200">
                                <td class="px-3 py-2 text-gray-700">CNAME</td>
                                <td class="px-3 py-2 text-purple-900 font-semibold">site</td>
                                <td class="px-3 py-2 text-purple-900 font-semibold">{{ getCustomDomainCnameTarget(currentSubdomain || 'your-subdomain', 'stores') }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div class="bg-white rounded-lg p-3 border border-purple-100">
                        <h6 class="text-sm font-medium text-purple-900 mb-2">Step 3: Verify Connection</h6>
                        <p class="text-xs text-purple-700">Once DNS propagation is complete (up to 48 hours), your custom domain will be active.</p>
                      </div>
                    </div>
                  </div>



                  <!-- Important Notes -->
                  <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h5 class="text-sm font-medium text-orange-800 mb-2 flex items-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                      </svg>
                      Important Notes
                    </h5>
                    <ul class="text-xs text-orange-700 space-y-1">
                      <li>• DNS changes can take up to 48 hours to propagate globally</li>
                      <li>• Ensure your domain provider supports CNAME records</li>
                      <li>• The CNAME record must point to your exact subdomain URL</li>
                      <li>• You can use any subdomain prefix (site, store, locator, etc.)</li>
                      <li>• SSL certificates are automatically provisioned for custom domains</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Header Tabs Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                  </svg>
                  Header Navigation Tabs
                </h4>
                <div class="space-y-4">
                  <p class="text-sm text-gray-600 mb-4">Configure the navigation tabs that appear in the header of your store locator.</p>
                  <div class="space-y-3">
                    <div v-for="(tab, index) in config.headerTabs" :key="index" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div class="flex-1">
                        <input
                          type="text"
                          v-model="tab.label"
                          placeholder="Tab Label (e.g., Overview)"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div class="flex-1">
                        <input
                          type="text"
                          v-model="tab.value"
                          placeholder="tab_value (e.g., overview)"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <button
                        @click="removeTab(index)"
                        class="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <button
                    @click="addTab"
                    class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
                  >
                    <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add Tab
                  </button>
                </div>
              </div>
            </div>

            <!-- Themes Tab -->
            <div v-if="activeTab === 'themes'" class="space-y-8">
              <!-- Theme Selection Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Pre-Designed Themes
                </h4>
                <p class="text-sm text-gray-600 mb-6">Choose from our professionally designed themes that automatically configure all styling options for your store locator.</p>
                
                <!-- Theme Categories -->
                <div class="mb-6">
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="category in themeCategories"
                      :key="category"
                      @click="selectedThemeCategory = category"
                      :class="[
                        'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                        selectedThemeCategory === category
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      ]"
                    >
                      {{ category }}
                    </button>
                  </div>
                </div>

                <!-- Theme Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div
                    v-for="(theme, themeKey) in filteredThemes"
                    :key="themeKey"
                    class="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                    :class="{ 'ring-2 ring-blue-500 border-blue-500': selectedTheme === themeKey }"
                    @click="selectTheme(themeKey)"
                  >
                    <!-- Theme Preview -->
                    <div class="mb-4">
                      <div class="flex items-center justify-between mb-2">
                        <h5 class="font-semibold text-gray-900">{{ theme.name }}</h5>
                        <span class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">{{ theme.category }}</span>
                      </div>
                      <p class="text-sm text-gray-600 mb-3">{{ theme.description }}</p>
                      
                      <!-- Color Preview -->
                      <div class="flex gap-2 mb-3">
                        <div
                          class="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                          :style="{ backgroundColor: theme.preview.primary }"
                          :title="`Primary: ${theme.preview.primary}`"
                        ></div>
                        <div
                          class="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                          :style="{ backgroundColor: theme.preview.secondary }"
                          :title="`Secondary: ${theme.preview.secondary}`"
                        ></div>
                        <div
                          class="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                          :style="{ backgroundColor: theme.preview.accent }"
                          :title="`Accent: ${theme.preview.accent}`"
                        ></div>
                      </div>
                    </div>

                    <!-- Apply Button -->
                    <button
                      @click.stop="applyTheme(themeKey)"
                      class="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
                    >
                      Apply Theme
                    </button>
                  </div>
                </div>

                <!-- Current Theme Info -->
                <div v-if="selectedTheme" class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div class="flex items-center justify-between">
                    <div>
                      <h5 class="text-sm font-medium text-blue-800">Current Theme</h5>
                      <p class="text-lg font-semibold text-blue-900">{{ themes[selectedTheme].name }}</p>
                      <p class="text-sm text-blue-700">{{ themes[selectedTheme].description }}</p>
                    </div>
                    <button
                      @click="resetToDefaults"
                      class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Reset to Default
                    </button>
                  </div>
                </div>
              </div>

              <!-- Theme Customization Info -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  Customization
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 class="text-sm font-medium text-gray-700 mb-2">After Applying a Theme</h5>
                    <ul class="text-sm text-gray-600 space-y-1">
                      <li>• You can further customize individual elements in other tabs</li>
                      <li>• All theme settings are applied instantly to the preview</li>
                      <li>• Changes are saved when you click "Save Configuration"</li>
                      <li>• You can always reset to the default theme</li>
                    </ul>
                  </div>
                  <div>
                    <h5 class="text-sm font-medium text-gray-700 mb-2">Theme Categories</h5>
                    <ul class="text-sm text-gray-600 space-y-1">
                      <li><strong>Professional:</strong> Corporate and business-focused themes</li>
                      <li><strong>Modern:</strong> Clean and contemporary designs</li>
                      <li><strong>Creative:</strong> Vibrant and artistic themes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <!-- Header Tab -->
            <div v-if="activeTab === 'header'" class="space-y-8">
              <!-- Header Background Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Header Background & Layout
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Header Background</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.header_styles.headerBg"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.headerBg"
                        placeholder="#f8fafc"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Background color for the header area</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Tab Button Style</label>
                    <select
                      v-model="selectedTabStyle"
                      @change="applyTabStyle"
                      class="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:border-gray-400 transition-colors font-medium"
                    >
                      <option value="default">Default Blue</option>
                      <option value="modern">Modern Gray</option>
                      <option value="elegant">Elegant Purple</option>
                      <option value="warm">Warm Orange</option>
                      <option value="nature">Nature Green</option>
                      <option value="minimal">Minimal White</option>
                      <option value="dark">Dark Theme</option>
                      <option value="primary-focus">Primary Focus</option>
                      <option value="secondary-focus">Secondary Focus</option>
                      <option value="primary-gradient">Primary Gradient</option>
                      <option value="secondary-gradient">Secondary Gradient</option>
                      <option value="primary-outline">Primary Outline</option>
                      <option value="secondary-outline">Secondary Outline</option>
                      <option value="primary-soft">Primary Soft</option>
                      <option value="secondary-soft">Secondary Soft</option>
                      <option value="primary-bold">Primary Bold</option>
                      <option value="secondary-bold">Secondary Bold</option>
                      <option value="custom">Custom (Manual)</option>
                    </select>
                    <p class="text-xs text-gray-500">Predefined tab button styling presets</p>
                  </div>
                </div>
              </div>

              <!-- Brand Identity Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Brand Identity
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Logo Size</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="logoSizeSlider"
                        min="1"
                        max="12"
                        step="0.1"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div class="flex flex-col items-center gap-1">
                        <input
                          type="text"
                          v-model="config.header_styles.logoSizeMobile"
                          placeholder="2.5rem"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-sm"
                        />
                        <span class="text-xs text-gray-500">Mobile</span>
                      </div>
                      <div class="flex flex-col items-center gap-1">
                        <input
                          type="text"
                          v-model="config.header_styles.logoSizeDesktop"
                          placeholder="5rem"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-sm"
                        />
                        <span class="text-xs text-gray-500">Desktop</span>
                      </div>
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current size: {{ logoSizeSlider }}rem</div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Brand Name Text Size</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="brandNameSizeSlider"
                        min="0.75"
                        max="3"
                        step="0.05"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.brandNameSize"
                        placeholder="1.375rem"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current size: {{ brandNameSizeSlider }}rem</div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Brand Name Text Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.header_styles.brandNameColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.brandNameColor"
                        placeholder="#000000"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Color for the brand name text</p>
                  </div>
                </div>
              </div>

              <!-- Search Bar Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                  Search Bar Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Search Border Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.header_styles.searchBorderColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.searchBorderColor"
                        placeholder="#1773EA"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Border color for the search input field</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Search Border Radius</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="searchBorderRadiusSlider"
                        min="0"
                        max="3"
                        step="0.1"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.searchBorderRadius"
                        placeholder="1.5rem"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current radius: {{ searchBorderRadiusSlider }}rem</div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Search Border Size</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="searchBorderSizeSlider"
                        min="0"
                        max="8"
                        step="0.5"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.searchBorderSize"
                        placeholder="2px"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current size: {{ searchBorderSizeSlider }}px</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sidepanel Tab -->
            <div v-if="activeTab === 'sidepanel'" class="space-y-8">
              <!-- Global Controls Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  Global Controls
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <!-- Border Radius Control -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Border Radius (All States)</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="sidepanelBorderRadiusSlider"
                        min="0"
                        max="2"
                        step="0.1"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.selectedRadius"
                        placeholder="1.25rem"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500">Current: {{ sidepanelBorderRadiusSlider }}rem</div>
                  </div>

                  <!-- Border Size Control -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Border Size (All States)</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="sidepanelBorderSizeSlider"
                        min="0"
                        max="8"
                        step="0.5"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.selectedBorderSize"
                        placeholder="3px"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500">Current: {{ sidepanelBorderSizeSlider }}px</div>
                  </div>

                  <!-- Style Preset Selector -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Style Preset</label>
                    <select
                      v-model="selectedSidepanelStyle"
                      @change="applySidepanelStyle"
                      class="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:border-gray-400 transition-colors font-medium"
                    >
                      <option value="primary-soft">Primary Soft</option>
                      <option value="primary-bold">Primary Bold</option>
                      <option value="primary-outline">Primary Outline</option>
                      <option value="secondary-soft">Secondary Soft</option>
                      <option value="secondary-bold">Secondary Bold</option>
                      <option value="secondary-outline">Secondary Outline</option>
                      <option value="minimal">Minimal</option>
                      <option value="elegant">Elegant</option>
                      <option value="custom">Custom (Manual)</option>
                    </select>
                    <p class="text-xs text-gray-500">Predefined styling presets</p>
                  </div>
                </div>
              </div>

              <!-- State-Specific Styling -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  State-Specific Styling
                </h4>
                
                <!-- Selected State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    Selected State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Background Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.selectedBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.selectedBg"
                          placeholder="#e6f7ff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.selectedText"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.selectedText"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.selectedBorder"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.selectedBorder"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Size</label>
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.selectedBorderSize"
                        placeholder="3px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Hover State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    Hover State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Background Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.hoverBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.hoverBg"
                          placeholder="#f0f7ff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.hoverText"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.hoverText"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.hoverBorder"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.hoverBorder"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Size</label>
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.hoverBorderSize"
                        placeholder="2px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Unselected State -->
                <div>
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                    Unselected State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Background Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.unselectedBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.unselectedBg"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.unselectedText"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.unselectedText"
                          placeholder="#222"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.unselectedBorder"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.unselectedBorder"
                          placeholder="#e0e7ef"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Size</label>
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.unselectedBorderSize"
                        placeholder="1px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Icons Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Icons & Visual Elements
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Show Icons</label>
                    <div class="flex items-center gap-3">
                      <label class="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          v-model="showIconsToggle"
                          class="sr-only"
                        />
                        <div class="relative">
                          <div class="block bg-gray-600 w-14 h-8 rounded-full"></div>
                          <div class="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition"></div>
                        </div>
                        <span class="ml-3 text-sm text-gray-700">{{ showIconsToggle ? 'Enabled' : 'Disabled' }}</span>
                      </label>
                    </div>
                    <p class="text-xs text-gray-500">Toggle visibility of location icons</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Icon Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.sidepanel_styles.iconColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.iconColor"
                        placeholder="#1773EA"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Chevron Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.sidepanel_styles.chevronColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.chevronColor"
                        placeholder="rgba(23, 115, 234, 0.7)"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Logo Tab -->
            <div v-if="activeTab === 'logo'" class="space-y-8">
              <!-- Logo Management Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Logo Management
                </h4>
                
                <!-- Current Logo Display -->
                <div v-if="config.logo_original" class="mb-6">
                  <h5 class="text-md font-medium text-gray-700 mb-3">Current Logo</h5>
                  <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-4">
                        <img 
                          :src="config.logo_original" 
                          alt="Logo Preview" 
                          class="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-white p-2"
                          @error="handleLogoError"
                        />
                        <div>
                          <p class="text-sm font-medium text-gray-900">Logo Active</p>
                          <p class="text-xs text-gray-500 mt-1">
                            {{ config.logo_original.startsWith('data:') ? 'Uploaded Image' : 'External URL' }}
                          </p>
                          <p v-if="!config.logo_original.startsWith('data:')" class="text-xs text-gray-400 mt-1 truncate max-w-xs">
                            {{ config.logo_original }}
                          </p>
                        </div>
                      </div>
                      <button
                        @click="removeLogo"
                        class="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm font-medium"
                      >
                        Remove Logo
                      </button>
                    </div>
                  </div>
                </div>

                <!-- No Logo State -->
                <div v-else class="mb-6">
                  <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div class="flex items-center">
                      <svg class="w-5 h-5 mr-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                      </svg>
                      <div>
                        <h5 class="text-sm font-medium text-yellow-800">No Logo Configured</h5>
                        <p class="text-sm text-yellow-700">Add a logo to enhance your store locator's branding</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Logo Upload Methods -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- URL Input Method -->
                  <div class="bg-white border border-gray-200 rounded-lg p-6">
                    <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                      <svg class="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                      </svg>
                      Add Logo from URL
                    </h5>
                    <div class="space-y-3">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
                        <input
                          v-model="logoUrlInput"
                          type="url"
                          placeholder="https://example.com/logo.png"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          @keyup.enter="setLogoFromUrl"
                        />
                      </div>
                      <button
                        @click="setLogoFromUrl"
                        :disabled="!logoUrlInput"
                        class="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                      >
                        Set Logo from URL
                      </button>
                      <p class="text-xs text-gray-500">Enter a direct URL to your logo image (PNG, JPG, SVG, etc.)</p>
                    </div>
                  </div>

                  <!-- File Upload Method -->
                  <div class="bg-white border border-gray-200 rounded-lg p-6">
                    <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                      <svg class="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                      </svg>
                      Upload Logo File
                    </h5>
                    <div class="space-y-3">
                      <div>
                        <input
                          ref="logoFileInput"
                          type="file"
                          accept="image/*"
                          @change="handleLogoFileUpload"
                          class="hidden"
                        />
                        <button
                          @click="$refs.logoFileInput.click()"
                          class="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        >
                          <div class="flex flex-col items-center py-4">
                            <svg class="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                            </svg>
                            <span class="text-sm font-medium text-gray-600">Click to upload</span>
                            <span class="text-xs text-gray-500">PNG, JPG, SVG up to 5MB</span>
                          </div>
                        </button>
                      </div>
                      
                      <!-- Upload Progress -->
                      <div v-if="logoUploadProgress > 0 && logoUploadProgress < 100" class="w-full">
                        <div class="flex items-center gap-2">
                          <div class="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              class="bg-green-500 h-2 rounded-full transition-all duration-300"
                              :style="{ width: logoUploadProgress + '%' }"
                            ></div>
                          </div>
                          <span class="text-xs text-gray-600">{{ logoUploadProgress }}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Logo Guidelines -->
                <div class="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-blue-800 mb-3 flex items-center">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Logo Guidelines
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
                    <div>
                      <h6 class="font-medium mb-2">Recommended Specifications:</h6>
                      <ul class="space-y-1 text-xs">
                        <li>• Format: PNG, JPG, SVG (SVG recommended for scalability)</li>
                        <li>• Size: 200x200px minimum, 500x500px recommended</li>
                        <li>• File size: Maximum 5MB</li>
                        <li>• Background: Transparent or white background preferred</li>
                      </ul>
                    </div>
                    <div>
                      <h6 class="font-medium mb-2">Best Practices:</h6>
                      <ul class="space-y-1 text-xs">
                        <li>• Use high-resolution images for crisp display</li>
                        <li>• Ensure logo is readable at small sizes</li>
                        <li>• Test how your logo looks on different backgrounds</li>
                        <li>• Keep file size optimized for faster loading</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details Panel Tab -->
            <div v-if="activeTab === 'details'" class="space-y-8">
              <!-- Panel Layout Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  Panel Layout & Appearance
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Background</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.bg"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.bg"
                        placeholder="#fff"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Background color for the details panel</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Text Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.textColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.textColor"
                        placeholder="#222"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Text color for panel content</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Border Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.borderColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.borderColor"
                        placeholder="#1976d2"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Border color for the panel outline</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Border Size</label>
                    <input
                      type="text"
                      v-model="config.details_styles.common.borderSize"
                      placeholder="2px"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p class="text-xs text-gray-500">Thickness of the panel border</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Border Radius</label>
                    <input
                      type="text"
                      v-model="config.details_styles.common.borderRadius"
                      placeholder="1.25rem"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p class="text-xs text-gray-500">Corner roundness of the panel</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Primary Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.primary"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.primary"
                        placeholder="#1976d2"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Primary accent color for highlights</p>
                  </div>
                </div>
              </div>

              <!-- Button Styling Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.122 2.122"></path>
                  </svg>
                  Button Styling & Interactions
                </h4>
                
                <!-- Global Button Controls -->
                <div class="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 class="text-md font-medium text-blue-800 mb-4 flex items-center">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    Global Button Controls
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Radius (All States)</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="range"
                          v-model="buttonBorderRadiusSlider"
                          min="0"
                          max="2"
                          step="0.1"
                          class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.borderRadius"
                          placeholder="0.75rem"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                        />
                      </div>
                      <div class="text-xs text-gray-500">Current: {{ buttonBorderRadiusSlider }}rem</div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Size (All States)</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="range"
                          v-model="buttonBorderSizeSlider"
                          min="0"
                          max="8"
                          step="0.5"
                          class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.borderSize"
                          placeholder="2px"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                        />
                      </div>
                      <div class="text-xs text-gray-500">Current: {{ buttonBorderSizeSlider }}px</div>
                    </div>
                  </div>
                </div>

                <!-- Default State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    Default State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.textColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.textColor"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Background</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.bg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.bg"
                          placeholder="#1976d2"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.borderColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.borderColor"
                          placeholder="#1976d2"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Size</label>
                      <input
                        type="text"
                        v-model="config.details_styles.button.borderSize"
                        placeholder="2px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Hover State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    Hover State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.hoverTextColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.hoverTextColor"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Background</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.hoverBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.hoverBg"
                          placeholder="#125bb5"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.hoverBorderColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.hoverBorderColor"
                          placeholder="#125bb5"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Border Size</label>
                      <input
                        type="text"
                        v-model="config.details_styles.button.hoverBorderSize"
                        placeholder="2.5px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Selected State -->
                <div>
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    Selected State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.selectedTextColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.selectedTextColor"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Background</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.selectedBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.selectedBg"
                          placeholder="#388e3c"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.selectedBorderColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.selectedBorderColor"
                          placeholder="#388e3c"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Border Size</label>
                      <input
                        type="text"
                        v-model="config.details_styles.button.selectedBorderSize"
                        placeholder="3px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>

          <!-- Action Buttons - Visible in every tab -->
          <div class="flex gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              @click="handleSaveClick"
              class="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
            >
              Save Configuration
            </button>
            <button
              @click="resetToDefaults"
              class="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium"
            >
              Reset to Defaults
            </button>
            <button
              @click="revertAllChanges"
              class="px-6 py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 font-medium"
            >
              Revert All Changes
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Dialogs -->
    <!-- Save Confirmation Dialog -->
    <div v-if="showSaveDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Save Configuration</h3>
        <p class="text-gray-600 mb-6">Are you sure you want to save all the configuration changes? This will update your store locator settings.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showSaveDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="confirmSave"
            class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>

    <!-- Back Confirmation Dialog -->
    <div v-if="showBackDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Unsaved Changes</h3>
        <p class="text-gray-600 mb-6">You have unsaved changes. Are you sure you want to go back? All unsaved changes will be lost.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showBackDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Stay Here
          </button>
          <button
            @click="confirmBack"
            class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>

    <!-- Subdomain Edit Dialog -->
    <div v-if="showSubdomainEditDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">
          {{ currentSubdomain ? 'Edit Subdomain' : 'Create Subdomain' }}
        </h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Subdomain Name</label>
            <div class="flex items-center">
              <input
                ref="subdomainInput"
                v-model="newSubdomain"
                type="text"
                placeholder="your-store-name"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :class="{ 'border-red-500': subdomainError }"
              />
              <span class="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600 text-sm">
                .{{ getDomainConfig().subdomainSuffix }}
              </span>
            </div>
            <p v-if="subdomainError" class="text-red-500 text-xs mt-1">{{ subdomainError }}</p>
                                  <p v-else class="text-gray-500 text-xs mt-1">
                        Your store locator will be accessible at: <strong>{{ getSubdomainUrl(newSubdomain || 'your-subdomain', 'stores') }}</strong>
                      </p>
          </div>

          <div v-if="subdomainAvailability" class="p-3 rounded-lg" :class="{
            'bg-green-50 border border-green-200 text-green-800': subdomainAvailability === 'available',
            'bg-red-50 border border-red-200 text-red-800': subdomainAvailability === 'unavailable',
            'bg-yellow-50 border border-yellow-200 text-yellow-800': subdomainAvailability === 'checking'
          }">
            <div class="flex items-center">
              <svg v-if="subdomainAvailability === 'available'" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <svg v-else-if="subdomainAvailability === 'unavailable'" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              <svg v-else class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              <span class="text-sm font-medium">
                {{ subdomainAvailability === 'available' ? 'Subdomain is available' : 
                   subdomainAvailability === 'unavailable' ? 'Subdomain is not available' : 
                   'Checking availability...' }}
              </span>
            </div>
          </div>

          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h5 class="text-sm font-medium text-blue-800 mb-2">Subdomain Requirements</h5>
            <ul class="text-xs text-blue-700 space-y-1">
              <li>• Only letters, numbers, and hyphens allowed</li>
              <li>• Must be between 3-63 characters</li>
              <li>• Cannot start or end with a hyphen</li>
              <li>• Cannot contain consecutive hyphens</li>
            </ul>
          </div>
        </div>

        <div class="flex gap-3 justify-end mt-6">
          <button
            @click="closeSubdomainDialog"
            :disabled="isSavingSubdomain"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="saveSubdomain"
            :disabled="!newSubdomain || subdomainError || subdomainAvailability !== 'available' || isSavingSubdomain"
            class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <span v-if="isSavingSubdomain" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
            <span v-else>{{ currentSubdomain ? 'Update Subdomain' : 'Create Subdomain' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Custom Domain Edit Dialog -->
    <div v-if="showCustomDomainDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-8 max-w-2xl w-full mx-4">
        <h3 class="text-xl font-semibold text-gray-900 mb-6">
          {{ currentCustomDomain ? 'Edit Custom Domain' : 'Connect Custom Domain' }}
        </h3>
        
        <div class="space-y-6">
          <!-- Domain Input -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Custom Domain</label>
            <input
              ref="customDomainInput"
              v-model="newCustomDomain"
              type="text"
                                      placeholder="stores.yourdomain.com"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              :class="{ 'border-red-500': customDomainError }"
            />
            <p v-if="customDomainError" class="text-red-500 text-sm mt-2">{{ customDomainError }}</p>
            <p v-else class="text-gray-500 text-sm mt-2">
                                    Enter your custom domain (e.g., stores.yourdomain.com)
            </p>
          </div>

          <!-- Domain Validation Status -->
          <div v-if="customDomainAvailability" class="p-4 rounded-lg" :class="{
            'bg-green-50 border border-green-200 text-green-800': customDomainAvailability === 'available',
            'bg-red-50 border border-red-200 text-red-800': customDomainAvailability === 'unavailable',
            'bg-yellow-50 border border-yellow-200 text-yellow-800': customDomainAvailability === 'checking'
          }">
            <div class="flex items-center">
              <svg v-if="customDomainAvailability === 'available'" class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <svg v-else-if="customDomainAvailability === 'unavailable'" class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              <svg v-else class="w-5 h-5 mr-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              <span class="text-sm font-medium">
                {{ customDomainAvailability === 'available' ? 'Domain format is valid' : 
                   customDomainAvailability === 'unavailable' ? 'Invalid domain format' : 
                   'Checking domain...' }}
              </span>
            </div>
            <!-- DNS Verification Note -->
            <div v-if="customDomainAvailability === 'available'" class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div class="flex items-start">
                <svg class="w-4 h-4 mr-2 mt-0.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="text-xs text-blue-700">
                  <p class="font-medium mb-1">Next Steps</p>
                  <p>Domain format is valid. Please configure your DNS records according to the instructions below to complete the setup.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- DNS Configuration Section -->
          <div class="bg-gray-50 rounded-lg p-6">
            <h4 class="text-lg font-semibold text-gray-900 mb-4">Configure your domain manually by adding this record</h4>
            
            <!-- DNS Records Table -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table class="w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RECORD TYPE</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HOST NAME</th>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REQUIRED VALUE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="border-t border-gray-200">
                    <td class="px-4 py-4 text-sm font-medium text-gray-900">CNAME</td>
                    <td class="px-4 py-4">
                      <div class="flex items-center">
                        <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-mono">{{ getDomainParts(newCustomDomain || 'stores.yourdomain.com')?.subdomain || 'stores' }}</span>
                        <button
                          @click="copyToClipboard(getDomainParts(newCustomDomain || 'stores.yourdomain.com')?.subdomain || 'stores')"
                          class="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td class="px-4 py-4">
                      <div class="flex items-center">
                        <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-mono">{{ getCustomDomainCnameTarget(currentSubdomain || 'your-subdomain', 'stores') }}</span>
                        <button
                          @click="copyToClipboard(getCustomDomainCnameTarget(currentSubdomain || 'your-subdomain', 'stores'))"
                          class="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>



            <!-- Forward Instructions -->
            <div class="mt-3">
              <button
                @click="forwardInstructions"
                class="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
              >
                >> Forward instructions to a colleague
                <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                </svg>
              </button>
            </div>

            <!-- Propagation Note -->
            <div class="mt-4 text-gray-500 text-sm">
              The changes may take up to 48 hours to reflect
            </div>
          </div>
        </div>

        <div class="flex gap-3 justify-end mt-8 pt-6 border-t border-gray-200">
          <button
            @click="closeCustomDomainDialog"
            :disabled="isSavingCustomDomain"
            class="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
          <button
            @click="saveCustomDomain"
            :disabled="!newCustomDomain || customDomainError || customDomainAvailability !== 'available' || isSavingCustomDomain"
            class="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
          >
            <span v-if="isSavingCustomDomain" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
            <span v-else>{{ currentCustomDomain ? 'Update Domain' : 'Connect Domain' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StoreLocator from '@/components/locations/StoreLocator.vue';
import EmbedWidget from '@/components/EmbedWidget.vue';
import { doc, updateDoc, getDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '@/utils/firebase.utils';
import { getSubdomainUrl, isValidCustomDomain, getCustomDomainCnameTarget, getDomainConfig } from '@/utils/domainConfig';

export default {
  name: 'StoreLocatorConfigurations',
  components: {
    StoreLocator,
    EmbedWidget
  },
  data() {
    return {
      showSaveDialog: false,
      showBackDialog: false,
      showSubdomainEditDialog: false,
      originalConfig: null,
      selectedTabStyle: 'default',
      selectedSidepanelStyle: 'primary-soft',
      showIconsToggle: true,
      activeTab: 'main',
      
      // Preview refresh
      isRefreshingPreview: false,
      previewKey: 0,
      storeRefreshKey: 0, // Key to force re-render of StoreLocator component

      // Subdomain management
      currentSubdomain: null,
      newSubdomain: '',
      subdomainError: '',
      subdomainAvailability: null,
      isSavingSubdomain: false,
      
      // Custom domain management
      currentCustomDomain: null,
      newCustomDomain: '',
      customDomainError: '',
      customDomainAvailability: null,
      isSavingCustomDomain: false,
      showCustomDomainDialog: false,
      configurationTabs: [
        { id: 'main', label: 'Main Settings' },
        { id: 'themes', label: 'Themes' },
        { id: 'header', label: 'Header' },
        { id: 'sidepanel', label: 'Sidepanel' },
        { id: 'details', label: 'Details Panel' },
        { id: 'logo', label: 'Logo' }
      ],
      selectedTheme: null,
      themes: {
        // Professional Themes
        'corporate-blue': {
          name: 'Corporate Blue',
          description: 'Professional and trustworthy blue theme for corporate businesses',
          category: 'Professional',
          preview: {
            primary: '#1e40af',
            secondary: '#64748b',
            accent: '#3b82f6'
          },
          config: {
            primaryColor: '#1e40af',
            secondaryColor: '#64748b',
            header_styles: {
              headerBg: '#f8fafc',
              tabTextColor: '#1e293b',
              tabHoverTextColor: '#1e40af',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#e2e8f0',
              tabHoverBg: '#dbeafe',
              tabSelectedBg: '#1e40af',
              searchBorderColor: '#1e40af',
              searchBorderRadius: '0.75rem',
              searchBorderSize: '2px',
              logoSizeMobile: '2.5rem',
              logoSizeDesktop: '3.5rem',
              brandNameSize: '1.25rem',
              brandNameColor: '#1e293b',
            },
            sidepanel_styles: {
              selectedBg: '#dbeafe',
              selectedText: '#1e40af',
              selectedBorder: '#1e40af',
              selectedBorderSize: '2px',
              selectedRadius: '0.75rem',
              hoverBg: '#eff6ff',
              hoverText: '#1e40af',
              hoverBorder: '#1e40af',
              hoverBorderSize: '1px',
              hoverRadius: '0.75rem',
              unselectedBg: '#ffffff',
              unselectedText: '#475569',
              unselectedBorder: '#e2e8f0',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.5rem',
              iconColor: '#1e40af',
              chevronColor: 'rgba(30, 64, 175, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#1e293b',
                borderColor: '#1e40af',
                borderSize: '2px',
                borderRadius: '0.75rem',
                primary: '#1e40af',
                secondary: '#64748b',
                accent: '#3b82f6',
                info: '#0ea5e9',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#1e40af',
                borderColor: '#1e40af',
                borderRadius: '0.5rem',
                borderSize: '2px',
                hoverTextColor: '#ffffff',
                hoverBg: '#1d4ed8',
                hoverBorderColor: '#1d4ed8',
                hoverBorderSize: '2px',
                selectedTextColor: '#ffffff',
                selectedBg: '#059669',
                selectedBorderColor: '#059669',
                selectedBorderSize: '2px',
              },
              products: {
                bg: '#f0f0f0',
                textColor: '#1e293b',
                borderColor: '#1e40af',
                borderSize: '2px',
                borderRadius: '0.75rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'modern-gray': {
          name: 'Modern Gray',
          description: 'Clean and minimal gray theme for modern businesses',
          category: 'Modern',
          preview: {
            primary: '#374151',
            secondary: '#6b7280',
            accent: '#9ca3af'
          },
          config: {
            primaryColor: '#374151',
            secondaryColor: '#6b7280',
            header_styles: {
              headerBg: '#ffffff',
              tabTextColor: '#374151',
              tabHoverTextColor: '#1f2937',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#f3f4f6',
              tabHoverBg: '#e5e7eb',
              tabSelectedBg: '#374151',
              searchBorderColor: '#d1d5db',
              searchBorderRadius: '0.5rem',
              searchBorderSize: '1px',
              logoSizeMobile: '2rem',
              logoSizeDesktop: '2.5rem',
              brandNameSize: '1.125rem',
              brandNameColor: '#111827',
            },
            sidepanel_styles: {
              selectedBg: '#f3f4f6',
              selectedText: '#374151',
              selectedBorder: '#d1d5db',
              selectedBorderSize: '1px',
              selectedRadius: '0.5rem',
              hoverBg: '#f9fafb',
              hoverText: '#374151',
              hoverBorder: '#e5e7eb',
              hoverBorderSize: '1px',
              hoverRadius: '0.5rem',
              unselectedBg: '#ffffff',
              unselectedText: '#6b7280',
              unselectedBorder: '#f3f4f6',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.375rem',
              iconColor: '#6b7280',
              chevronColor: 'rgba(107, 114, 128, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#374151',
                borderColor: '#d1d5db',
                borderSize: '1px',
                borderRadius: '0.5rem',
                primary: '#374151',
                secondary: '#6b7280',
                accent: '#9ca3af',
                info: '#3b82f6',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#374151',
                borderColor: '#374151',
                borderRadius: '0.375rem',
                borderSize: '1px',
                hoverTextColor: '#ffffff',
                hoverBg: '#1f2937',
                hoverBorderColor: '#1f2937',
                hoverBorderSize: '1px',
                selectedTextColor: '#ffffff',
                selectedBg: '#059669',
                selectedBorderColor: '#059669',
                selectedBorderSize: '1px',
              },
              products: {
                bg: '#f9fafb',
                textColor: '#374151',
                borderColor: '#d1d5db',
                borderSize: '1px',
                borderRadius: '0.5rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'vibrant-orange': {
          name: 'Vibrant Orange',
          description: 'Energetic and bold orange theme for creative businesses',
          category: 'Creative',
          preview: {
            primary: '#ea580c',
            secondary: '#f97316',
            accent: '#fb923c'
          },
          config: {
            primaryColor: '#ea580c',
            secondaryColor: '#f97316',
            header_styles: {
              headerBg: '#fff7ed',
              tabTextColor: '#9a3412',
              tabHoverTextColor: '#ea580c',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#fed7aa',
              tabHoverBg: '#fdba74',
              tabSelectedBg: '#ea580c',
              searchBorderColor: '#ea580c',
              searchBorderRadius: '1rem',
              searchBorderSize: '2px',
              logoSizeMobile: '3rem',
              logoSizeDesktop: '4rem',
              brandNameSize: '1.5rem',
              brandNameColor: '#9a3412',
            },
            sidepanel_styles: {
              selectedBg: '#fed7aa',
              selectedText: '#ea580c',
              selectedBorder: '#ea580c',
              selectedBorderSize: '2px',
              selectedRadius: '1rem',
              hoverBg: '#fdba74',
              hoverText: '#ea580c',
              hoverBorder: '#ea580c',
              hoverBorderSize: '1px',
              hoverRadius: '0.75rem',
              unselectedBg: '#ffffff',
              unselectedText: '#9a3412',
              unselectedBorder: '#fed7aa',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.5rem',
              iconColor: '#ea580c',
              chevronColor: 'rgba(234, 88, 12, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#9a3412',
                borderColor: '#ea580c',
                borderSize: '2px',
                borderRadius: '1rem',
                primary: '#ea580c',
                secondary: '#f97316',
                accent: '#fb923c',
                info: '#3b82f6',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#ea580c',
                borderColor: '#ea580c',
                borderRadius: '0.75rem',
                borderSize: '2px',
                hoverTextColor: '#ffffff',
                hoverBg: '#c2410c',
                hoverBorderColor: '#c2410c',
                hoverBorderSize: '2px',
                selectedTextColor: '#ffffff',
                selectedBg: '#059669',
                selectedBorderColor: '#059669',
                selectedBorderSize: '2px',
              },
              products: {
                bg: '#fff7ed',
                textColor: '#9a3412',
                borderColor: '#ea580c',
                borderSize: '2px',
                borderRadius: '1rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'elegant-purple': {
          name: 'Elegant Purple',
          description: 'Sophisticated purple theme for luxury and premium brands',
          category: 'Professional',
          preview: {
            primary: '#7c3aed',
            secondary: '#a855f7',
            accent: '#c084fc'
          },
          config: {
            primaryColor: '#7c3aed',
            secondaryColor: '#a855f7',
            header_styles: {
              headerBg: '#faf5ff',
              tabTextColor: '#4c1d95',
              tabHoverTextColor: '#7c3aed',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#f3e8ff',
              tabHoverBg: '#e9d5ff',
              tabSelectedBg: '#7c3aed',
              searchBorderColor: '#7c3aed',
              searchBorderRadius: '1.25rem',
              searchBorderSize: '2px',
              logoSizeMobile: '2.75rem',
              logoSizeDesktop: '3.5rem',
              brandNameSize: '1.375rem',
              brandNameColor: '#4c1d95',
            },
            sidepanel_styles: {
              selectedBg: '#f3e8ff',
              selectedText: '#7c3aed',
              selectedBorder: '#7c3aed',
              selectedBorderSize: '2px',
              selectedRadius: '1rem',
              hoverBg: '#e9d5ff',
              hoverText: '#7c3aed',
              hoverBorder: '#7c3aed',
              hoverBorderSize: '1px',
              hoverRadius: '0.75rem',
              unselectedBg: '#ffffff',
              unselectedText: '#6b21a8',
              unselectedBorder: '#f3e8ff',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.5rem',
              iconColor: '#7c3aed',
              chevronColor: 'rgba(124, 58, 237, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#4c1d95',
                borderColor: '#7c3aed',
                borderSize: '2px',
                borderRadius: '1rem',
                primary: '#7c3aed',
                secondary: '#a855f7',
                accent: '#c084fc',
                info: '#3b82f6',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#7c3aed',
                borderColor: '#7c3aed',
                borderRadius: '0.75rem',
                borderSize: '2px',
                hoverTextColor: '#ffffff',
                hoverBg: '#6d28d9',
                hoverBorderColor: '#6d28d9',
                hoverBorderSize: '2px',
                selectedTextColor: '#ffffff',
                selectedBg: '#059669',
                selectedBorderColor: '#059669',
                selectedBorderSize: '2px',
              },
              products: {
                bg: '#faf5ff',
                textColor: '#4c1d95',
                borderColor: '#7c3aed',
                borderSize: '2px',
                borderRadius: '1rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'nature-green': {
          name: 'Nature Green',
          description: 'Fresh and organic green theme for eco-friendly businesses',
          category: 'Creative',
          preview: {
            primary: '#16a34a',
            secondary: '#22c55e',
            accent: '#4ade80'
          },
          config: {
            primaryColor: '#16a34a',
            secondaryColor: '#22c55e',
            header_styles: {
              headerBg: '#f0fdf4',
              tabTextColor: '#166534',
              tabHoverTextColor: '#16a34a',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#dcfce7',
              tabHoverBg: '#bbf7d0',
              tabSelectedBg: '#16a34a',
              searchBorderColor: '#16a34a',
              searchBorderRadius: '1rem',
              searchBorderSize: '2px',
              logoSizeMobile: '2.5rem',
              logoSizeDesktop: '3.25rem',
              brandNameSize: '1.25rem',
              brandNameColor: '#166534',
            },
            sidepanel_styles: {
              selectedBg: '#dcfce7',
              selectedText: '#16a34a',
              selectedBorder: '#16a34a',
              selectedBorderSize: '2px',
              selectedRadius: '0.75rem',
              hoverBg: '#bbf7d0',
              hoverText: '#16a34a',
              hoverBorder: '#16a34a',
              hoverBorderSize: '1px',
              hoverRadius: '0.5rem',
              unselectedBg: '#ffffff',
              unselectedText: '#166534',
              unselectedBorder: '#dcfce7',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.375rem',
              iconColor: '#16a34a',
              chevronColor: 'rgba(22, 163, 74, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#166534',
                borderColor: '#16a34a',
                borderSize: '2px',
                borderRadius: '0.75rem',
                primary: '#16a34a',
                secondary: '#22c55e',
                accent: '#4ade80',
                info: '#0ea5e9',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#16a34a',
                borderColor: '#16a34a',
                borderRadius: '0.5rem',
                borderSize: '2px',
                hoverTextColor: '#ffffff',
                hoverBg: '#15803d',
                hoverBorderColor: '#15803d',
                hoverBorderSize: '2px',
                selectedTextColor: '#ffffff',
                selectedBg: '#059669',
                selectedBorderColor: '#059669',
                selectedBorderSize: '2px',
              },
              products: {
                bg: '#f0fdf4',
                textColor: '#166534',
                borderColor: '#16a34a',
                borderSize: '2px',
                borderRadius: '0.75rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'dark-mode': {
          name: 'Dark Mode',
          description: 'Modern dark theme for contemporary applications',
          category: 'Modern',
          preview: {
            primary: '#3b82f6',
            secondary: '#64748b',
            accent: '#60a5fa'
          },
          config: {
            primaryColor: '#3b82f6',
            secondaryColor: '#64748b',
            header_styles: {
              headerBg: '#1f2937',
              tabTextColor: '#d1d5db',
              tabHoverTextColor: '#f9fafb',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#374151',
              tabHoverBg: '#4b5563',
              tabSelectedBg: '#3b82f6',
              searchBorderColor: '#3b82f6',
              searchBorderRadius: '0.75rem',
              searchBorderSize: '2px',
              logoSizeMobile: '2.5rem',
              logoSizeDesktop: '3.25rem',
              brandNameSize: '1.25rem',
              brandNameColor: '#f9fafb',
            },
            sidepanel_styles: {
              selectedBg: '#1e3a8a',
              selectedText: '#3b82f6',
              selectedBorder: '#3b82f6',
              selectedBorderSize: '2px',
              selectedRadius: '0.75rem',
              hoverBg: '#1e40af',
              hoverText: '#3b82f6',
              hoverBorder: '#3b82f6',
              hoverBorderSize: '1px',
              hoverRadius: '0.5rem',
              unselectedBg: '#374151',
              unselectedText: '#9ca3af',
              unselectedBorder: '#4b5563',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.375rem',
              iconColor: '#3b82f6',
              chevronColor: 'rgba(59, 130, 246, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#374151',
                textColor: '#f9fafb',
                borderColor: '#3b82f6',
                borderSize: '2px',
                borderRadius: '0.75rem',
                primary: '#3b82f6',
                secondary: '#64748b',
                accent: '#60a5fa',
                info: '#0ea5e9',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#3b82f6',
                borderColor: '#3b82f6',
                borderRadius: '0.5rem',
                borderSize: '2px',
                hoverTextColor: '#ffffff',
                hoverBg: '#2563eb',
                hoverBorderColor: '#2563eb',
                hoverBorderSize: '2px',
                selectedTextColor: '#ffffff',
                selectedBg: '#059669',
                selectedBorderColor: '#059669',
                selectedBorderSize: '2px',
              },
              products: {
                bg: '#374151',
                textColor: '#f9fafb',
                borderColor: '#3b82f6',
                borderSize: '2px',
                borderRadius: '0.75rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'light-mode': {
          name: 'Light Mode',
          description: 'Clean and bright light theme for modern applications',
          category: 'Modern',
          preview: {
            primary: '#1773ea',
            secondary: '#6b7280',
            accent: '#3b82f6'
          },
          config: {
            primaryColor: '#1773ea',
            secondaryColor: '#6b7280',
            header_styles: {
              headerBg: '#ffffff',
              tabTextColor: '#374151',
              tabHoverTextColor: '#1773ea',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#f3f4f6',
              tabHoverBg: '#dbeafe',
              tabSelectedBg: '#1773ea',
              searchBorderColor: '#1773ea',
              searchBorderRadius: '0.75rem',
              searchBorderSize: '2px',
              logoSizeMobile: '2.5rem',
              logoSizeDesktop: '3.25rem',
              brandNameSize: '1.25rem',
              brandNameColor: '#111827',
            },
            sidepanel_styles: {
              selectedBg: '#dbeafe',
              selectedText: '#1773ea',
              selectedBorder: '#1773ea',
              selectedBorderSize: '2px',
              selectedRadius: '0.75rem',
              hoverBg: '#eff6ff',
              hoverText: '#1773ea',
              hoverBorder: '#1773ea',
              hoverBorderSize: '1px',
              hoverRadius: '0.5rem',
              unselectedBg: '#ffffff',
              unselectedText: '#6b7280',
              unselectedBorder: '#e5e7eb',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.375rem',
              iconColor: '#1773ea',
              chevronColor: 'rgba(23, 115, 234, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#374151',
                borderColor: '#1773ea',
                borderSize: '2px',
                borderRadius: '0.75rem',
                primary: '#1773ea',
                secondary: '#6b7280',
                accent: '#3b82f6',
                info: '#0ea5e9',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#1773ea',
                borderColor: '#1773ea',
                borderRadius: '0.5rem',
                borderSize: '2px',
                hoverTextColor: '#ffffff',
                hoverBg: '#1d4ed8',
                hoverBorderColor: '#1d4ed8',
                hoverBorderSize: '2px',
                selectedTextColor: '#ffffff',
                selectedBg: '#059669',
                selectedBorderColor: '#059669',
                selectedBorderSize: '2px',
              },
              products: {
                bg: '#ffffff',
                textColor: '#374151',
                borderColor: '#1773ea',
                borderSize: '2px',
                borderRadius: '0.75rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'minimalist': {
          name: 'Minimalist',
          description: 'Ultra-clean minimalist theme with subtle styling',
          category: 'Modern',
          preview: {
            primary: '#374151',
            secondary: '#9ca3af',
            accent: '#6b7280'
          },
          config: {
            primaryColor: '#374151',
            secondaryColor: '#9ca3af',
            header_styles: {
              headerBg: '#ffffff',
              tabTextColor: '#6b7280',
              tabHoverTextColor: '#374151',
              tabSelectedTextColor: '#374151',
              tabBg: '#ffffff',
              tabHoverBg: '#f9fafb',
              tabSelectedBg: '#f3f4f6',
              searchBorderColor: '#d1d5db',
              searchBorderRadius: '0.5rem',
              searchBorderSize: '1px',
              logoSizeMobile: '2rem',
              logoSizeDesktop: '2.5rem',
              brandNameSize: '1.125rem',
              brandNameColor: '#111827',
            },
            sidepanel_styles: {
              selectedBg: '#f9fafb',
              selectedText: '#374151',
              selectedBorder: '#d1d5db',
              selectedBorderSize: '1px',
              selectedRadius: '0.5rem',
              hoverBg: '#f3f4f6',
              hoverText: '#374151',
              hoverBorder: '#e5e7eb',
              hoverBorderSize: '1px',
              hoverRadius: '0.5rem',
              unselectedBg: '#ffffff',
              unselectedText: '#9ca3af',
              unselectedBorder: '#f3f4f6',
              unselectedBorderSize: '1px',
              unselectedRadius: '0.375rem',
              iconColor: '#9ca3af',
              chevronColor: 'rgba(156, 163, 175, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#374151',
                borderColor: '#d1d5db',
                borderSize: '1px',
                borderRadius: '0.5rem',
                primary: '#374151',
                secondary: '#9ca3af',
                accent: '#6b7280',
                info: '#3b82f6',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#374151',
                bg: '#ffffff',
                borderColor: '#d1d5db',
                borderRadius: '0.375rem',
                borderSize: '1px',
                hoverTextColor: '#374151',
                hoverBg: '#f9fafb',
                hoverBorderColor: '#9ca3af',
                hoverBorderSize: '1px',
                selectedTextColor: '#ffffff',
                selectedBg: '#374151',
                selectedBorderColor: '#374151',
                selectedBorderSize: '1px',
              },
              products: {
                bg: '#ffffff',
                textColor: '#374151',
                borderColor: '#d1d5db',
                borderSize: '1px',
                borderRadius: '0.5rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        },
        'vibrant': {
          name: 'Vibrant',
          description: 'Bold and energetic theme with vibrant colors',
          category: 'Creative',
          preview: {
            primary: '#f59e0b',
            secondary: '#ef4444',
            accent: '#8b5cf6'
          },
          config: {
            primaryColor: '#f59e0b',
            secondaryColor: '#ef4444',
            header_styles: {
              headerBg: '#fef3c7',
              tabTextColor: '#92400e',
              tabHoverTextColor: '#f59e0b',
              tabSelectedTextColor: '#ffffff',
              tabBg: '#fed7aa',
              tabHoverBg: '#fdba74',
              tabSelectedBg: '#f59e0b',
              searchBorderColor: '#f59e0b',
              searchBorderRadius: '1.25rem',
              searchBorderSize: '3px',
              logoSizeMobile: '3rem',
              logoSizeDesktop: '4rem',
              brandNameSize: '1.5rem',
              brandNameColor: '#92400e',
            },
            sidepanel_styles: {
              selectedBg: '#fed7aa',
              selectedText: '#f59e0b',
              selectedBorder: '#f59e0b',
              selectedBorderSize: '3px',
              selectedRadius: '1.25rem',
              hoverBg: '#fdba74',
              hoverText: '#f59e0b',
              hoverBorder: '#f59e0b',
              hoverBorderSize: '2px',
              hoverRadius: '1rem',
              unselectedBg: '#ffffff',
              unselectedText: '#92400e',
              unselectedBorder: '#fed7aa',
              unselectedBorderSize: '2px',
              unselectedRadius: '0.75rem',
              iconColor: '#f59e0b',
              chevronColor: 'rgba(245, 158, 11, 0.7)',
              showIcons: 'all',
            },
            details_styles: {
              common: {
                bg: '#ffffff',
                textColor: '#92400e',
                borderColor: '#f59e0b',
                borderSize: '3px',
                borderRadius: '1.25rem',
                primary: '#f59e0b',
                secondary: '#ef4444',
                accent: '#8b5cf6',
                info: '#3b82f6',
                success: '#059669',
                warning: '#d97706',
                error: '#dc2626',
              },
              button: {
                textColor: '#ffffff',
                bg: '#f59e0b',
                borderColor: '#f59e0b',
                borderRadius: '1rem',
                borderSize: '3px',
                hoverTextColor: '#ffffff',
                hoverBg: '#d97706',
                hoverBorderColor: '#d97706',
                hoverBorderSize: '3px',
                selectedTextColor: '#ffffff',
                selectedBg: '#ef4444',
                selectedBorderColor: '#ef4444',
                selectedBorderSize: '3px',
              },
              products: {
                bg: '#fef3c7',
                textColor: '#92400e',
                borderColor: '#f59e0b',
                borderSize: '3px',
                borderRadius: '1.25rem',
              },
              headerTabs: [
                { label: 'Home', value: this.getWebsiteUrl() || 'home' }
              ]
            }
          }
        }
      },
      config: {
        primaryColor: '#1773EA',
        secondaryColor: '#6B7280',
        logo_original: null,
        header_styles: {
          headerBg: '#f8fafc',
          tabTextColor: '#222',
          tabHoverTextColor: '#1773EA',
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: '#1773EA',
          searchBorderColor: '#1773EA',
          searchBorderRadius: '1.5rem',
          searchBorderSize: '2px',
          logoSizeMobile: '2.5rem',
          logoSizeDesktop: '5rem',
          brandNameSize: '1.375rem',
          brandNameColor: '#000000',
        },
        sidepanel_styles: {
          selectedBg: '#e6f7ff',
          selectedText: '#1773EA',
          selectedBorder: '#1773EA',
          selectedBorderSize: '3px',
          selectedRadius: '1.25rem',
          hoverBg: '#f0f7ff',
          hoverText: '#1773EA',
          hoverBorder: '#1773EA',
          hoverBorderSize: '2px',
          hoverRadius: '1.25rem',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px',
          unselectedRadius: '0.75rem',
          iconColor: '#1773EA',
          chevronColor: 'rgba(23, 115, 234, 0.7)',
          showIcons: 'all',
        },
        headerTabs: [
          { label: 'Home', value: this.getWebsiteUrl() || 'home' }
        ],
        details_styles: {
          common: {
            bg: '#fff',
            textColor: '#222',
            borderColor: '#1976d2',
            borderSize: '2px',
            borderRadius: '1.25rem',
            primary: '#1976d2',
            secondary: '#424242',
            accent: '#82B1FF',
            info: '#2196F3',
            success: '#4CAF50',
            warning: '#FFC107',
            error: '#FF5252',
          },
          button: {
            textColor: '#fff',
            bg: '#1976d2',
            borderColor: '#1976d2',
            borderRadius: '0.75rem',
            borderSize: '2px',
            hoverTextColor: '#fff',
            hoverBg: '#125bb5',
            hoverBorderColor: '#125bb5',
            hoverBorderSize: '2.5px',
            selectedTextColor: '#fff',
            selectedBg: '#388e3c',
            selectedBorderColor: '#388e3c',
            selectedBorderSize: '3px',
          }
        }
      },
      selectedThemeCategory: 'All',
      themeCategories: ['All', 'Professional', 'Modern', 'Creative'],
      
      // Logo management
      logoUrlInput: '',
      logoUploadProgress: 0
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser;
    },
    currentUID() {
      return this.$store.getters.currentUID;
    },
    WID() {
      return this.$store.getters.WID;
    },
    hasUnsavedChanges() {
      if (!this.originalConfig) return false;
      return JSON.stringify(this.config) !== JSON.stringify(this.originalConfig);
    },
    logoSizeSlider: {
      get() {
        // Use the average of mobile and desktop sizes for the slider
        const mobileSize = parseFloat(this.config.header_styles.logoSizeMobile) || 2.5;
        const desktopSize = parseFloat(this.config.header_styles.logoSizeDesktop) || 5;
        return (mobileSize + desktopSize) / 2;
      },
      set(value) {
        // Set both mobile and desktop sizes based on the slider value
        // Mobile is 60% of the slider value, desktop is 140% of the slider value
        const mobileSize = Math.max(1, value * 0.6);
        const desktopSize = Math.max(2, value * 1.4);
        this.config.header_styles.logoSizeMobile = `${mobileSize.toFixed(1)}rem`;
        this.config.header_styles.logoSizeDesktop = `${desktopSize.toFixed(1)}rem`;
      }
    },
    brandNameSizeSlider: {
      get() {
        // Convert "1.375rem" to 1.375, handle various input formats
        const value = this.config.header_styles.brandNameSize;
        if (!value) return 1.375;
        
        // Remove 'rem' and parse the number
        const numericValue = parseFloat(value.replace('rem', ''));
        return isNaN(numericValue) ? 1.375 : numericValue;
      },
      set(value) {
        // Convert 1.375 to "1.375rem"
        this.config.header_styles.brandNameSize = `${value}rem`;
      }
    },
    searchBorderRadiusSlider: {
      get() {
        // Convert "1.5rem" to 1.5
        const value = this.config.header_styles.searchBorderRadius;
        return parseFloat(value) || 1.5;
      },
      set(value) {
        // Convert 1.5 to "1.5rem"
        this.config.header_styles.searchBorderRadius = `${value}rem`;
      }
    },
    searchBorderSizeSlider: {
      get() {
        // Convert "2px" to 2
        const value = this.config.header_styles.searchBorderSize;
        return parseFloat(value) || 2;
      },
      set(value) {
        // Convert 2 to "2px"
        this.config.header_styles.searchBorderSize = `${value}px`;
      }
    },
    sidepanelBorderRadiusSlider: {
      get() {
        // Convert "1.25rem" to 1.25
        const value = this.config.sidepanel_styles.selectedRadius;
        return parseFloat(value) || 1.25;
      },
      set(value) {
        // Convert 1.25 to "1.25rem" and apply to all border radius values
        const radiusValue = `${value}rem`;
        this.config.sidepanel_styles.selectedRadius = radiusValue;
        this.config.sidepanel_styles.hoverRadius = radiusValue;
        this.config.sidepanel_styles.unselectedRadius = radiusValue;
      }
    },
    sidepanelBorderSizeSlider: {
      get() {
        // Convert "3px" to 3
        const value = this.config.sidepanel_styles.selectedBorderSize;
        return parseFloat(value) || 3;
      },
      set(value) {
        // Convert 3 to "3px" and apply to all border size values
        const sizeValue = `${value}px`;
        this.config.sidepanel_styles.selectedBorderSize = sizeValue;
        this.config.sidepanel_styles.hoverBorderSize = sizeValue;
        this.config.sidepanel_styles.unselectedBorderSize = sizeValue;
      }
    },
    buttonBorderRadiusSlider: {
      get() {
        // Convert "0.75rem" to 0.75
        const value = this.config.details_styles.button.borderRadius;
        return parseFloat(value.replace('rem', '')) || 0.75;
      },
      set(value) {
        // Convert 0.75 to "0.75rem" and apply to all button border radius values
        const radiusValue = `${value}rem`;
        this.config.details_styles.button.borderRadius = radiusValue;
        this.config.details_styles.button.hoverBorderRadius = radiusValue;
        this.config.details_styles.button.selectedBorderRadius = radiusValue;
      }
    },
    buttonBorderSizeSlider: {
      get() {
        // Convert "2px" to 2
        const value = this.config.details_styles.button.borderSize;
        return parseFloat(value) || 2;
      },
      set(value) {
        // Convert 2 to "2px" and apply to all button border size values
        const sizeValue = `${value}px`;
        this.config.details_styles.button.borderSize = sizeValue;
        this.config.details_styles.button.hoverBorderSize = sizeValue;
        this.config.details_styles.button.selectedBorderSize = sizeValue;
      }
    },
    filteredThemes() {
      if (this.selectedThemeCategory === 'All') {
        return this.themes;
      }
      return Object.fromEntries(
        Object.entries(this.themes).filter(([key, theme]) => 
          theme.category === this.selectedThemeCategory
        )
      );
    }
  },
  watch: {
    'config.primaryColor': {
      handler(newColor) {
        // Reapply tab style when primary color changes
        if (this.selectedTabStyle && this.selectedTabStyle !== 'custom') {
          this.applyTabStyle();
        }
        // Reapply sidepanel style when primary color changes
        if (this.selectedSidepanelStyle && this.selectedSidepanelStyle !== 'custom') {
          this.applySidepanelStyle();
        }
      }
    },
    'config.secondaryColor': {
      handler(newColor) {
        // Reapply tab style when secondary color changes
        if (this.selectedTabStyle && this.selectedTabStyle !== 'custom') {
          this.applyTabStyle();
        }
        // Reapply sidepanel style when secondary color changes
        if (this.selectedSidepanelStyle && this.selectedSidepanelStyle !== 'custom') {
          this.applySidepanelStyle();
        }
      }
    },
    showIconsToggle: {
      handler(newValue) {
        this.config.sidepanel_styles.showIcons = newValue ? 'all' : 'none';
      }
    },
    config: {
      handler(newConfig) {
        // Refresh the preview when configuration changes
        this.$nextTick(() => {
          this.storeRefreshKey++;
        });
      },
      deep: true
    }
  },
  methods: {
    async refreshPreview() {
      try {
        this.isRefreshingPreview = true;
        
        // Force re-render of the StoreLocator component by changing the key
        this.storeRefreshKey++;
        
        // Add a small delay to show the refresh animation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        this.$snackbar.show('Preview refreshed successfully!', 'success');
      } catch (error) {
        console.error('Error refreshing preview:', error);
        this.$snackbar.show('Error refreshing preview', 'error');
      } finally {
        this.isRefreshingPreview = false;
      }
    },
    
    getSubdomainUrl(subdomainName, type = 'stores') {
      return getSubdomainUrl(subdomainName, type);
    },
    getDomainConfig() {
      return getDomainConfig();
    },
    getWebsiteUrl() {
      // Try to get from workspace data first
      if (this.workspaceData?.website) {
        return this.workspaceData.website;
      }
      
      // Try to get from locations if available
      if (this.locations && this.locations.length > 0 && this.locations[0]?.website) {
        return this.locations[0].website;
      }
      
      return null;
    },
    addTab() {
      this.config.headerTabs.push({ label: 'New Tab', value: 'new_tab' });
    },
    removeTab(index) {
      this.config.headerTabs.splice(index, 1);
    },
    handleSaveClick() {
      this.showSaveDialog = true;
    },
    async confirmSave() {
      this.showSaveDialog = false;
      await this.saveConfiguration();
    },
    async saveConfiguration() {
      try {
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        
        // Prepare the data to save
        const dataToSave = {
          store_locator_styles: this.config
        };
        
        // If logo_original exists in config, save it separately
        if (this.config.logo_original) {
          dataToSave.logo_original = this.config.logo_original;
        }
        
        await updateDoc(workspaceRef, dataToSave);
        
        // Update original config after successful save
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
        
        this.$snackbar.show('Configuration saved successfully!', 'success');
      } catch (error) {
        console.error('Error saving configuration:', error);
        this.$snackbar.show('Error saving configuration', 'error');
      }
    },
    handleBackClick() {
      if (this.hasUnsavedChanges) {
        this.showBackDialog = true;
      } else {
        this.goBack();
      }
    },
    confirmBack() {
      this.showBackDialog = false;
      this.goBack();
    },
    goBack() {
      this.$router.go(-1);
    },
    revertAllChanges() {
      if (this.originalConfig) {
        this.config = JSON.parse(JSON.stringify(this.originalConfig));
        this.logoUrlInput = '';
        this.logoUploadProgress = 0;
        this.$snackbar.show('All changes reverted!', 'success');
      } else {
        this.$snackbar.show('No changes to revert', 'info');
      }
    },
    async loadConfiguration() {
      try {
        console.log('🔍 [loadConfiguration] Starting configuration load...');
        
        if (!this.currentUID || !this.WID) {
          console.error('❌ [loadConfiguration] Missing required data:', {
            currentUID: this.currentUID,
            WID: this.WID
          });
          return;
        }
        
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        
        console.log('🔍 [loadConfiguration] Workspace document exists:', workspaceDoc.exists());
        
        if (workspaceDoc.exists()) {
          const workspaceData = workspaceDoc.data();
          console.log('🔍 [loadConfiguration] Workspace data keys:', Object.keys(workspaceData));
          
          if (workspaceData.store_locator_styles) {
            console.log('✅ [loadConfiguration] Found store locator styles');
            this.config = { ...this.config, ...workspaceData.store_locator_styles };
          } else {
            console.log('⚠️ [loadConfiguration] No store locator styles found, using defaults');
          }
          
          // Load logo_original if it exists
          if (workspaceData.logo_original) {
            console.log('✅ [loadConfiguration] Found logo_original');
            this.config.logo_original = workspaceData.logo_original;
          }
        } else {
          console.warn('⚠️ [loadConfiguration] Workspace document does not exist');
        }
        
        // Detect current tab style
        this.detectCurrentTabStyle();
        
        // Detect current sidepanel style
        this.detectCurrentSidepanelStyle();
        
        // Set showIconsToggle based on current config
        this.showIconsToggle = this.config.sidepanel_styles.showIcons === 'all';
        
        // Detect current theme
        this.detectCurrentTheme();
        
        // Store original config for comparison
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
        
        console.log('✅ [loadConfiguration] Configuration loaded successfully');
      } catch (error) {
        console.error('❌ [loadConfiguration] Error loading configuration:', error);
        this.$snackbar.show('Error loading configuration. Please try refreshing the page.', 'error');
      }
    },
    detectCurrentTabStyle() {
      const headerStyles = this.config.header_styles || {};
      
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      const tabStyles = {
        default: {
          tabTextColor: '#222',
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: primaryColor
        },
        modern: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3F4F6',
          tabHoverBg: '#E5E7EB',
          tabSelectedBg: '#6B7280'
        },
        elegant: {
          tabTextColor: '#4C1D95',
          tabHoverTextColor: '#7C3AED',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3E8FF',
          tabHoverBg: '#E9D5FF',
          tabSelectedBg: '#7C3AED'
        },
        warm: {
          tabTextColor: '#92400E',
          tabHoverTextColor: '#EA580C',
          tabSelectedTextColor: '#fff',
          tabBg: '#FEF3C7',
          tabHoverBg: '#FED7AA',
          tabSelectedBg: '#EA580C'
        },
        nature: {
          tabTextColor: '#166534',
          tabHoverTextColor: '#16A34A',
          tabSelectedTextColor: '#fff',
          tabBg: '#DCFCE7',
          tabHoverBg: '#BBF7D0',
          tabSelectedBg: '#16A34A'
        },
        minimal: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#1F2937',
          tabBg: '#fff',
          tabHoverBg: '#F9FAFB',
          tabSelectedBg: '#F3F4F6'
        },
        dark: {
          tabTextColor: '#D1D5DB',
          tabHoverTextColor: '#F9FAFB',
          tabSelectedTextColor: '#fff',
          tabBg: '#374151',
          tabHoverBg: '#4B5563',
          tabSelectedBg: '#1F2937'
        },
        'primary-focus': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-focus': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-gradient': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`
        },
        'secondary-gradient': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`
        },
        'primary-outline': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-outline': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-soft': {
          tabTextColor: primaryColor,
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F0F7FF',
          tabHoverBg: '#DBEAFE',
          tabSelectedBg: primaryColor
        },
        'secondary-soft': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: secondaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: '#F3F4F6',
          tabSelectedBg: secondaryColor
        },
        'primary-bold': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: getDarkerShade(primaryColor),
          tabSelectedBg: getDarkerShade(primaryColor)
        },
        'secondary-bold': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: getDarkerShade(secondaryColor),
          tabSelectedBg: getDarkerShade(secondaryColor)
        }
      };

      // Check if current tab styles match any predefined style
      for (const [styleName, style] of Object.entries(tabStyles)) {
        if (
          headerStyles.tabTextColor === style.tabTextColor &&
          headerStyles.tabHoverTextColor === style.tabHoverTextColor &&
          headerStyles.tabSelectedTextColor === style.tabSelectedTextColor &&
          headerStyles.tabBg === style.tabBg &&
          headerStyles.tabHoverBg === style.tabHoverBg &&
          headerStyles.tabSelectedBg === style.tabSelectedBg
        ) {
          this.selectedTabStyle = styleName;
          return;
        }
      }
      
      // If no match found, set to custom
      this.selectedTabStyle = 'custom';
    },
    resetToDefaults() {
      this.selectedTabStyle = 'default';
      this.selectedSidepanelStyle = 'primary-soft';
      this.showIconsToggle = true;
      this.logoUrlInput = '';
      this.logoUploadProgress = 0;
      this.config = {
        primaryColor: '#1773EA',
        secondaryColor: '#6B7280',
        logo_original: null,
        header_styles: {
          headerBg: '#f8fafc',
          tabTextColor: '#222',
          tabHoverTextColor: '#1773EA',
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: '#1773EA',
          searchBorderColor: '#1773EA',
          searchBorderRadius: '1.5rem',
          searchBorderSize: '2px',
          logoSizeMobile: '2.5rem',
          logoSizeDesktop: '5rem',
          brandNameSize: '1.375rem',
          brandNameColor: '#000000',
        },
        sidepanel_styles: {
          selectedBg: '#e6f7ff',
          selectedText: '#1773EA',
          selectedBorder: '#1773EA',
          selectedBorderSize: '3px',
          selectedRadius: '1.25rem',
          hoverBg: '#f0f7ff',
          hoverText: '#1773EA',
          hoverBorder: '#1773EA',
          hoverBorderSize: '2px',
          hoverRadius: '1.25rem',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px',
          unselectedRadius: '0.75rem',
          iconColor: '#1773EA',
          chevronColor: 'rgba(23, 115, 234, 0.7)',
          showIcons: 'all',
        },
        headerTabs: [
          { label: 'Home', value: this.getWebsiteUrl() || 'home' }
        ],
        details_styles: {
          common: {
            bg: '#fff',
            textColor: '#222',
            borderColor: '#1976d2',
            borderSize: '2px',
            borderRadius: '1.25rem',
            primary: '#1976d2',
            secondary: '#424242',
            accent: '#82B1FF',
            info: '#2196F3',
            success: '#4CAF50',
            warning: '#FFC107',
            error: '#FF5252',
          },
          button: {
            textColor: '#fff',
            bg: '#1976d2',
            borderColor: '#1976d2',
            borderRadius: '0.75rem',
            borderSize: '2px',
            hoverTextColor: '#fff',
            hoverBg: '#125bb5',
            hoverBorderColor: '#125bb5',
            hoverBorderSize: '2.5px',
            selectedTextColor: '#fff',
            selectedBg: '#388e3c',
            selectedBorderColor: '#388e3c',
            selectedBorderSize: '3px',
          },
          products: {
            bg: '#f0f0f0',
            textColor: '#222',
            borderColor: '#1773EA',
            borderSize: '2px',
            borderRadius: '1.25rem',
          }
        }
      };
    },
    applyTabStyle() {
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      const tabStyles = {
        default: {
          tabTextColor: '#222',
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: primaryColor
        },
        modern: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3F4F6',
          tabHoverBg: '#E5E7EB',
          tabSelectedBg: '#6B7280'
        },
        elegant: {
          tabTextColor: '#4C1D95',
          tabHoverTextColor: '#7C3AED',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3E8FF',
          tabHoverBg: '#E9D5FF',
          tabSelectedBg: '#7C3AED'
        },
        warm: {
          tabTextColor: '#92400E',
          tabHoverTextColor: '#EA580C',
          tabSelectedTextColor: '#fff',
          tabBg: '#FEF3C7',
          tabHoverBg: '#FED7AA',
          tabSelectedBg: '#EA580C'
        },
        nature: {
          tabTextColor: '#166534',
          tabHoverTextColor: '#16A34A',
          tabSelectedTextColor: '#fff',
          tabBg: '#DCFCE7',
          tabHoverBg: '#BBF7D0',
          tabSelectedBg: '#16A34A'
        },
        minimal: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#1F2937',
          tabBg: '#fff',
          tabHoverBg: '#F9FAFB',
          tabSelectedBg: '#F3F4F6'
        },
        dark: {
          tabTextColor: '#D1D5DB',
          tabHoverTextColor: '#F9FAFB',
          tabSelectedTextColor: '#fff',
          tabBg: '#374151',
          tabHoverBg: '#4B5563',
          tabSelectedBg: '#1F2937'
        },
        'primary-focus': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-focus': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-gradient': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`
        },
        'secondary-gradient': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`
        },
        'primary-outline': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-outline': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-soft': {
          tabTextColor: primaryColor,
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F0F7FF',
          tabHoverBg: '#DBEAFE',
          tabSelectedBg: primaryColor
        },
        'secondary-soft': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: secondaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: '#F3F4F6',
          tabSelectedBg: secondaryColor
        },
        'primary-bold': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: getDarkerShade(primaryColor),
          tabSelectedBg: getDarkerShade(primaryColor)
        },
        'secondary-bold': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: getDarkerShade(secondaryColor),
          tabSelectedBg: getDarkerShade(secondaryColor)
        }
      };

      if (this.selectedTabStyle !== 'custom' && tabStyles[this.selectedTabStyle]) {
        const style = tabStyles[this.selectedTabStyle];
        this.config.header_styles.tabTextColor = style.tabTextColor;
        this.config.header_styles.tabHoverTextColor = style.tabHoverTextColor;
        this.config.header_styles.tabSelectedTextColor = style.tabSelectedTextColor;
        this.config.header_styles.tabBg = style.tabBg;
        this.config.header_styles.tabHoverBg = style.tabHoverBg;
        this.config.header_styles.tabSelectedBg = style.tabSelectedBg;
      }
    },
    applySidepanelStyle() {
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      // Helper function to get lighter shade of a color
      const getLighterShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = (num >> 8 & 0x00FF) + amt;
          const B = (num & 0x0000FF) + amt;
          return '#' + (0x1000000 + (R > 255 ? 255 : R) * 0x10000 +
            (G > 255 ? 255 : G) * 0x100 +
            (B > 255 ? 255 : B)).toString(16).slice(1);
        }
        return color;
      };

      const sidepanelStyles = {
        'primary-soft': {
          selectedBg: getLighterShade(primaryColor, 30),
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 20),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-bold': {
          selectedBg: primaryColor,
          selectedText: '#fff',
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(primaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(primaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-outline': {
          selectedBg: 'transparent',
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 40),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-soft': {
          selectedBg: getLighterShade(secondaryColor, 30),
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 20),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-bold': {
          selectedBg: secondaryColor,
          selectedText: '#fff',
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(secondaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(secondaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-outline': {
          selectedBg: 'transparent',
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 40),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'minimal': {
          selectedBg: '#f8fafc',
          selectedText: '#1f2937',
          selectedBorder: '#d1d5db',
          selectedBorderSize: '2px',
          hoverBg: '#f1f5f9',
          hoverText: '#1f2937',
          hoverBorder: '#cbd5e1',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#6b7280',
          unselectedBorder: '#e5e7eb',
          unselectedBorderSize: '1px'
        },
        'elegant': {
          selectedBg: '#faf5ff',
          selectedText: '#7c3aed',
          selectedBorder: '#7c3aed',
          selectedBorderSize: '3px',
          hoverBg: '#f3e8ff',
          hoverText: '#7c3aed',
          hoverBorder: '#7c3aed',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#374151',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        }
      };

      if (this.selectedSidepanelStyle !== 'custom' && sidepanelStyles[this.selectedSidepanelStyle]) {
        const style = sidepanelStyles[this.selectedSidepanelStyle];
        this.config.sidepanel_styles.selectedBg = style.selectedBg;
        this.config.sidepanel_styles.selectedText = style.selectedText;
        this.config.sidepanel_styles.selectedBorder = style.selectedBorder;
        this.config.sidepanel_styles.selectedBorderSize = style.selectedBorderSize;
        this.config.sidepanel_styles.hoverBg = style.hoverBg;
        this.config.sidepanel_styles.hoverText = style.hoverText;
        this.config.sidepanel_styles.hoverBorder = style.hoverBorder;
        this.config.sidepanel_styles.hoverBorderSize = style.hoverBorderSize;
        this.config.sidepanel_styles.unselectedBg = style.unselectedBg;
        this.config.sidepanel_styles.unselectedText = style.unselectedText;
        this.config.sidepanel_styles.unselectedBorder = style.unselectedBorder;
        this.config.sidepanel_styles.unselectedBorderSize = style.unselectedBorderSize;
      }
    },
    detectCurrentSidepanelStyle() {
      const sidepanelStyles = this.config.sidepanel_styles || {};
      
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      // Helper function to get lighter shade of a color
      const getLighterShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = (num >> 8 & 0x00FF) + amt;
          const B = (num & 0x0000FF) + amt;
          return '#' + (0x1000000 + (R > 255 ? 255 : R) * 0x10000 +
            (G > 255 ? 255 : G) * 0x100 +
            (B > 255 ? 255 : B)).toString(16).slice(1);
        }
        return color;
      };

      const stylePresets = {
        'primary-soft': {
          selectedBg: getLighterShade(primaryColor, 30),
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 20),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-bold': {
          selectedBg: primaryColor,
          selectedText: '#fff',
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(primaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(primaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-outline': {
          selectedBg: 'transparent',
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 40),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-soft': {
          selectedBg: getLighterShade(secondaryColor, 30),
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 20),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-bold': {
          selectedBg: secondaryColor,
          selectedText: '#fff',
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(secondaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(secondaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-outline': {
          selectedBg: 'transparent',
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 40),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'minimal': {
          selectedBg: '#f8fafc',
          selectedText: '#1f2937',
          selectedBorder: '#d1d5db',
          selectedBorderSize: '2px',
          hoverBg: '#f1f5f9',
          hoverText: '#1f2937',
          hoverBorder: '#cbd5e1',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#6b7280',
          unselectedBorder: '#e5e7eb',
          unselectedBorderSize: '1px'
        },
        'elegant': {
          selectedBg: '#faf5ff',
          selectedText: '#7c3aed',
          selectedBorder: '#7c3aed',
          selectedBorderSize: '3px',
          hoverBg: '#f3e8ff',
          hoverText: '#7c3aed',
          hoverBorder: '#7c3aed',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#374151',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        }
      };

      // Check if current sidepanel styles match any predefined style
      for (const [styleName, style] of Object.entries(stylePresets)) {
        if (
          sidepanelStyles.selectedBg === style.selectedBg &&
          sidepanelStyles.selectedText === style.selectedText &&
          sidepanelStyles.selectedBorder === style.selectedBorder &&
          sidepanelStyles.selectedBorderSize === style.selectedBorderSize &&
          sidepanelStyles.hoverBg === style.hoverBg &&
          sidepanelStyles.hoverText === style.hoverText &&
          sidepanelStyles.hoverBorder === style.hoverBorder &&
          sidepanelStyles.hoverBorderSize === style.hoverBorderSize &&
          sidepanelStyles.unselectedBg === style.unselectedBg &&
          sidepanelStyles.unselectedText === style.unselectedText &&
          sidepanelStyles.unselectedBorder === style.unselectedBorder &&
          sidepanelStyles.unselectedBorderSize === style.unselectedBorderSize
        ) {
          this.selectedSidepanelStyle = styleName;
          return;
        }
      }
      
      // If no match found, set to custom
      this.selectedSidepanelStyle = 'custom';
    },
    
    // Subdomain management methods
    async loadSubdomainData() {
      try {
        // Load current subdomain from workspace
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists()) {
          const workspaceData = workspaceDoc.data();
          this.currentSubdomain = workspaceData.subdomain || null;
        }
      } catch (error) {
        console.error('Error loading subdomain data:', error);
      }
    },
    
    validateSubdomain(subdomain) {
      if (!subdomain) {
        return 'Subdomain is required';
      }
      
      if (subdomain.length < 3) {
        return 'Subdomain must be at least 3 characters long';
      }
      
      if (subdomain.length > 63) {
        return 'Subdomain must be less than 63 characters';
      }
      
      if (!/^[a-z0-9-]+$/.test(subdomain)) {
        return 'Subdomain can only contain lowercase letters, numbers, and hyphens';
      }
      
      if (subdomain.startsWith('-') || subdomain.endsWith('-')) {
        return 'Subdomain cannot start or end with a hyphen';
      }
      
      if (subdomain.includes('--')) {
        return 'Subdomain cannot contain consecutive hyphens';
      }
      
      return null;
    },
    
    async checkSubdomainAvailability(subdomain) {
      if (!subdomain) {
        this.subdomainAvailability = null;
        return;
      }
      
      const error = this.validateSubdomain(subdomain);
      if (error) {
        this.subdomainError = error;
        this.subdomainAvailability = 'unavailable';
        return;
      }
      
      this.subdomainError = '';
      this.subdomainAvailability = 'checking';
      
      try {
        // Check if subdomain exists in unique_domain collection
        // This collection stores both store locator and individual store subdomains
        // We use the 'type' field to distinguish between them
        const domainRef = doc(db, 'unique_domain', subdomain);
        const domainDoc = await getDoc(domainRef);
        
        if (domainDoc.exists()) {
          const domainData = domainDoc.data();
          // Check if this subdomain belongs to the current workspace and is a store locator
          if (domainData.uid === this.currentUID && domainData.wid === this.WID && domainData.type === 'store_locator') {
            this.subdomainAvailability = 'available';
          } else {
            this.subdomainAvailability = 'unavailable';
          }
        } else {
          // Subdomain doesn't exist in unique_domain collection, so it's available
          this.subdomainAvailability = 'available';
        }
      } catch (error) {
        console.error('Error checking subdomain availability:', error);
        this.subdomainAvailability = 'unavailable';
      }
    },
    
    async saveSubdomain() {
      if (!this.newSubdomain || this.subdomainError || this.subdomainAvailability !== 'available') {
        return;
      }
      
      this.isSavingSubdomain = true;
      
      try {
        const oldSubdomain = this.currentSubdomain;
        const newSubdomain = this.newSubdomain.toLowerCase().trim();
        
        // Validate the subdomain one more time
        const validationError = this.validateSubdomain(newSubdomain);
        if (validationError) {
          this.subdomainError = validationError;
          this.subdomainAvailability = 'unavailable';
          return;
        }
        
        // Double-check uniqueness in unique_domain collection before saving
        if (newSubdomain !== oldSubdomain) {
          const domainRef = doc(db, 'unique_domain', newSubdomain);
          const domainDoc = await getDoc(domainRef);
          
          if (domainDoc.exists()) {
            const domainData = domainDoc.data();
            if (domainData.uid !== this.currentUID || domainData.wid !== this.WID || domainData.type !== 'store_locator') {
              this.subdomainError = 'This subdomain is already taken by another workspace or store';
              this.subdomainAvailability = 'unavailable';
              return;
            }
          }
        }
        
        // Update workspace with new subdomain
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        await updateDoc(workspaceRef, {
          subdomain: newSubdomain
        });
        
        // If there was an old subdomain and it's different from the new one, delete it from unique_domain collection
        if (oldSubdomain && oldSubdomain !== newSubdomain) {
          const oldDomainRef = doc(db, 'unique_domain', oldSubdomain);
          await deleteDoc(oldDomainRef);
        }
        
        // Create new subdomain document in unique_domain collection
        const newDomainRef = doc(db, 'unique_domain', newSubdomain);
        await setDoc(newDomainRef, {
          uid: this.currentUID,
          wid: this.WID,
          type: 'store_locator', // Distinguish from individual store subdomains
          created_at: new Date(),
          updated_at: new Date()
        });
        
        // Update current subdomain
        this.currentSubdomain = newSubdomain;
        
        // Close dialog and reset form
        this.showSubdomainEditDialog = false;
        this.newSubdomain = '';
        this.subdomainError = '';
        this.subdomainAvailability = null;
        
        this.$snackbar.show(`Subdomain ${oldSubdomain ? 'updated' : 'created'} successfully! Your store locator is now accessible at ${this.getSubdomainUrl(newSubdomain, 'stores')}`, 'success');
      } catch (error) {
        console.error('Error saving subdomain:', error);
        this.$snackbar.show('Error updating subdomain. Please try again.', 'error');
      } finally {
        this.isSavingSubdomain = false;
      }
    },
    
    openSubdomainDialog() {
      this.newSubdomain = this.currentSubdomain || '';
      this.subdomainError = '';
      this.subdomainAvailability = null;
      console.log("hi0")
      this.showSubdomainEditDialog = true;
      
      // Focus and select the input field after dialog opens
      console.log("hi1")
      this.$nextTick(() => {
        if (this.$refs.subdomainInput) {
          this.$refs.subdomainInput.focus();
        }
        console.log("hi2")
        // Check availability after a short delay
        if (this.newSubdomain) {
          this.checkSubdomainAvailability(this.newSubdomain);
        }
      });
    },
    
    closeSubdomainDialog() {
      this.showSubdomainEditDialog = false;
      this.newSubdomain = '';
      this.subdomainError = '';
      this.subdomainAvailability = null;
    },
    
    // Custom domain management methods
    async loadCustomDomainData() {
      try {
        // Load current custom domain from workspace
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists()) {
          const workspaceData = workspaceDoc.data();
          this.currentCustomDomain = workspaceData.custom_domain || null;
        }
      } catch (error) {
        console.error('Error loading custom domain data:', error);
      }
    },
    
    validateCustomDomain(domain) {
      if (!domain) {
        return 'Domain is required';
      }
      
      if (!isValidCustomDomain(domain)) {
        return 'Please enter a valid domain format (e.g., stores.yourdomain.com)';
      }
      
      return null;
    },
    
    async checkCustomDomainAvailability(domain) {
      if (!domain) {
        this.customDomainAvailability = null;
        return;
      }
      
      const error = this.validateCustomDomain(domain);
      if (error) {
        this.customDomainError = error;
        this.customDomainAvailability = 'unavailable';
        return;
      }
      
      this.customDomainError = '';
      this.customDomainAvailability = 'checking';
      
      try {
        // TODO: Implement actual DNS verification functionality
        // Currently only validating domain format
        // Future enhancement: Verify DNS records are properly configured
        // and pointing to the correct target before allowing domain connection
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate check
        
        this.customDomainAvailability = 'available';
      } catch (error) {
        console.error('Error checking custom domain availability:', error);
        this.customDomainAvailability = 'unavailable';
      }
    },
    
    async saveCustomDomain() {
      if (!this.newCustomDomain || this.customDomainError || this.customDomainAvailability !== 'available') {
        return;
      }
      
      this.isSavingCustomDomain = true;
      
      try {
        const oldCustomDomain = this.currentCustomDomain;
        const newCustomDomain = this.newCustomDomain.toLowerCase().trim();
        
        // Validate the domain one more time
        const validationError = this.validateCustomDomain(newCustomDomain);
        if (validationError) {
          this.customDomainError = validationError;
          this.customDomainAvailability = 'unavailable';
          return;
        }
        
        // Update workspace with new custom domain
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        await updateDoc(workspaceRef, {
          custom_domain: newCustomDomain
        });
        
        // Update current custom domain
        this.currentCustomDomain = newCustomDomain;
        
        // Close dialog and reset form
        this.showCustomDomainDialog = false;
        this.newCustomDomain = '';
        this.customDomainError = '';
        this.customDomainAvailability = null;
        
        this.$snackbar.show(`Custom domain ${oldCustomDomain ? 'updated' : 'connected'} successfully! Please configure your DNS settings as shown in the instructions. Note: DNS verification will be implemented in a future update.`, 'success');
      } catch (error) {
        console.error('Error saving custom domain:', error);
        this.$snackbar.show('Error updating custom domain. Please try again.', 'error');
      } finally {
        this.isSavingCustomDomain = false;
      }
    },
    
    openCustomDomainDialog() {
      this.newCustomDomain = this.currentCustomDomain || '';
      this.customDomainError = '';
      this.customDomainAvailability = null;
      this.showCustomDomainDialog = true;
      
      // Focus and select the input field after dialog opens
      this.$nextTick(() => {
        if (this.$refs.customDomainInput) {
          this.$refs.customDomainInput.focus();
        }
        
        // Check availability after a short delay
        if (this.newCustomDomain) {
          this.checkCustomDomainAvailability(this.newCustomDomain);
        }
      });
    },
    
    closeCustomDomainDialog() {
      this.showCustomDomainDialog = false;
      this.newCustomDomain = '';
      this.customDomainError = '';
      this.customDomainAvailability = null;
      this.recordsCopied = 0;
    },
    
    // Helper methods for the new dialog
    getDomainParts(domain) {
      if (!domain) return null;
      const parts = domain.split('.');
      if (parts.length < 2) return null;
      return {
        subdomain: parts[0],
        domain: parts.slice(1).join('.')
      };
    },
    
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$snackbar.show('Copied to clipboard!', 'success');
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        this.$snackbar.show('Failed to copy to clipboard', 'error');
      }
    },
    
    forwardInstructions() {
      const instructions = `DNS Configuration for ${this.newCustomDomain || 'your domain'}:

CNAME Record:
- HOST NAME: ${this.getDomainParts(this.newCustomDomain || 'stores.yourdomain.com')?.subdomain || 'stores'}
- REQUIRED VALUE: ${this.getCustomDomainCnameTarget(this.currentSubdomain || 'your-subdomain', 'stores')}

Note: The changes may take up to 48 hours to reflect.`;
      
      // Create a shareable link or copy instructions
      if (navigator.share) {
        navigator.share({
          title: 'DNS Configuration Instructions',
          text: instructions
        });
      } else {
        this.copyToClipboard(instructions);
      }
    },
    
    // Theme-related methods
    selectTheme(themeKey) {
      this.selectedTheme = themeKey;
    },
    
    applyTheme(themeKey) {
      if (!this.themes[themeKey]) {
        console.error('Theme not found:', themeKey);
        return;
      }
      
      const theme = this.themes[themeKey];
      
      // Apply the theme configuration
      this.config = {
        ...this.config,
        ...theme.config
      };
      
      // Update selected theme
      this.selectedTheme = themeKey;
      
      // Update related style selections
      this.selectedTabStyle = 'custom';
      this.selectedSidepanelStyle = 'custom';
      
      // Update showIconsToggle based on theme
      this.showIconsToggle = theme.config.sidepanel_styles.showIcons === 'all';
      
      // Show success message
      this.$snackbar.show(`${theme.name} theme applied successfully!`, 'success');
      
      console.log('✅ [applyTheme] Theme applied:', themeKey, theme.name);
    },
    
    detectCurrentTheme() {
      // Try to detect which theme is currently applied
      for (const [themeKey, theme] of Object.entries(this.themes)) {
        const themeConfig = theme.config;
        
        // Check if current config matches this theme
        if (
          this.config.primaryColor === themeConfig.primaryColor &&
          this.config.secondaryColor === themeConfig.secondaryColor &&
          JSON.stringify(this.config.header_styles) === JSON.stringify(themeConfig.header_styles) &&
          JSON.stringify(this.config.sidepanel_styles) === JSON.stringify(themeConfig.sidepanel_styles) &&
          JSON.stringify(this.config.details_styles) === JSON.stringify(themeConfig.details_styles)
        ) {
          this.selectedTheme = themeKey;
          return;
        }
      }
      
      // If no exact match found, set to null (custom theme)
      this.selectedTheme = null;
    },
    
    // Logo management methods
    async setLogoFromUrl() {
      if (!this.logoUrlInput) {
        this.$snackbar.show('Please enter a logo URL', 'error');
        return;
      }
      
      // Validate URL format
      try {
        new URL(this.logoUrlInput);
      } catch (error) {
        this.$snackbar.show('Please enter a valid URL', 'error');
        return;
      }
      
      try {
        // Set the logo URL
        this.config.logo_original = this.logoUrlInput;
        this.logoUrlInput = '';
        
        // Save to backend immediately
        await this.saveLogoToBackend();
        
        this.$snackbar.show('Logo URL set and saved successfully!', 'success');
      } catch (error) {
        console.error('Error saving logo URL:', error);
        this.$snackbar.show('Error saving logo. Please try again.', 'error');
        // Revert the change if save failed
        this.config.logo_original = null;
      }
    },
    
    async handleLogoFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.$snackbar.show('Please select an image file', 'error');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$snackbar.show('File size must be less than 5MB', 'error');
        return;
      }
      
      try {
        this.logoUploadProgress = 10;
        
        // Convert file to base64
        const base64 = await this.fileToBase64(file);
        this.logoUploadProgress = 50;
        
        // Set the logo data
        this.config.logo_original = base64;
        this.logoUploadProgress = 75;
        
        // Save to backend immediately
        await this.saveLogoToBackend();
        this.logoUploadProgress = 100;
        
        // Reset file input
        event.target.value = '';
        
        this.$snackbar.show('Logo uploaded and saved successfully!', 'success');
        
        // Reset progress after a delay
        setTimeout(() => {
          this.logoUploadProgress = 0;
        }, 1000);
        
      } catch (error) {
        console.error('Error uploading logo:', error);
        this.$snackbar.show('Error uploading logo. Please try again.', 'error');
        this.logoUploadProgress = 0;
        // Revert the change if save failed
        this.config.logo_original = null;
      }
    },
    
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    
    async removeLogo() {
      try {
        this.config.logo_original = null;
        
        // Save to backend immediately
        await this.saveLogoToBackend();
        
        this.$snackbar.show('Logo removed and saved successfully!', 'success');
      } catch (error) {
        console.error('Error removing logo:', error);
        this.$snackbar.show('Error removing logo. Please try again.', 'error');
        // Revert the change if save failed
        this.config.logo_original = this.originalConfig?.logo_original || null;
      }
    },
    
    handleLogoError(event) {
      // If logo fails to load, remove it
      this.config.logo_original = null;
      this.$snackbar.show('Logo failed to load and has been removed', 'error');
    },
    
    async saveLogoToBackend() {
      try {
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        
        // Prepare the data to save
        const dataToSave = {
          logo_original: this.config.logo_original
        };
        
        await updateDoc(workspaceRef, dataToSave);
        
        // Update original config after successful save
        if (this.originalConfig) {
          this.originalConfig.logo_original = this.config.logo_original;
        }
        
        console.log('✅ Logo saved to backend successfully');
      } catch (error) {
        console.error('❌ Error saving logo to backend:', error);
        throw error; // Re-throw to be handled by calling method
      }
    },
    
    getCustomDomainCnameTarget
  },
  watch: {
    newSubdomain: {
      handler(newValue) {
        if (newValue) {
          this.checkSubdomainAvailability(newValue);
        } else {
          this.subdomainError = '';
          this.subdomainAvailability = null;
        }
      },
      immediate: false
    },
    newCustomDomain: {
      handler(newValue) {
        if (newValue) {
          this.checkCustomDomainAvailability(newValue);
        } else {
          this.customDomainError = '';
          this.customDomainAvailability = null;
        }
      },
      immediate: false
    },
    'config.logo_original': {
      handler(newValue) {
        // Trigger change detection for logo updates
        console.log('Logo updated:', newValue ? 'set' : 'removed');
      }
    }
  },
  mounted() {
    try {
      console.log('🚀 [mounted] Starting StoreLocatorConfigurations initialization...');
      
      // Check if required data is available
      if (!this.currentUID || !this.WID) {
        console.error('❌ [mounted] Missing required data:', {
          currentUID: this.currentUID,
          WID: this.WID
        });
        this.$snackbar.show('Missing user or workspace data. Please refresh the page.', 'error');
        return;
      }
      
      console.log('✅ [mounted] Required data available, loading configuration...');
      this.loadConfiguration();
      this.loadSubdomainData();
      this.loadCustomDomainData();
      
      console.log('✅ [mounted] StoreLocatorConfigurations initialization completed');
    } catch (error) {
      console.error('❌ [mounted] Error during initialization:', error);
      this.$snackbar.show('Error initializing component. Please refresh the page.', 'error');
    }
  }
};
</script>

<style scoped>
/* Slider styling */
.slider {
  -webkit-appearance: none;
  appearance: none;
  background: #e5e7eb;
  outline: none;
  border-radius: 0.5rem;
  transition: background 0.2s;
}

.slider:hover {
  background: #d1d5db;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1773EA;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.slider::-webkit-slider-thumb:hover {
  background: #125bb5;
  transform: scale(1.1);
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1773EA;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.slider::-moz-range-thumb:hover {
  background: #125bb5;
  transform: scale(1.1);
}

/* Add any additional styles here */

/* Toggle switch styles */
input[type="checkbox"]:checked + .relative .block {
  background-color: #1773EA;
}

input[type="checkbox"]:checked + .relative .dot {
  transform: translateX(100%);
}

.relative .block {
  transition: background-color 0.3s ease;
}

.relative .dot {
  transition: transform 0.3s ease;
}

/* Enhanced dropdown styling */
select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

select:focus {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%231773ea' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
}

/* Style preset dropdown specific styling */
select[class*="border-2"] {
  position: relative;
  background-color: #ffffff;
  border: 2px solid #d1d5db;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

select[class*="border-2"]:hover {
  border-color: #9ca3af;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

select[class*="border-2"]:focus {
  border-color: #1773ea;
  box-shadow: 0 0 0 3px rgba(23, 115, 234, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style> 